import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { validateUaeMobile, UaeMobileInput, MOBILE_ERROR } from "@/components/AddressForm";

export function GuestAuthDialog({ open, onOpenChange, mode, setMode, onSuccess }) {
  const { login, register } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "", mobile: "" });
  const [mobileErr, setMobileErr] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        if (!f.name || !f.email || !f.password || !f.mobile) { toast.error("Please fill all fields"); setLoading(false); return; }
        const m = validateUaeMobile(f.mobile);
        if (!m.ok) { setMobileErr(MOBILE_ERROR); setLoading(false); return; }
        setMobileErr("");
        if (!termsAccepted) { toast.error("Please accept the Terms & Conditions and Privacy Policy"); setLoading(false); return; }
        const u = await register({ ...f, mobile: m.normalized, emirate: "Dubai", area: "", user_type: "both", language_preference: "English", terms_accepted: termsAccepted });
        onSuccess?.(u);
      } else {
        const u = await login(f.email, f.password);
        onSuccess?.(u);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || (mode === "register" ? "Registration failed" : "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="guest-auth-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "register" ? "Create your free account" : "Log in to publish"}</DialogTitle>
          <DialogDescription>Your listing will publish automatically after you {mode === "register" ? "sign up" : "log in"}.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-3">
          {mode === "register" && (
            <div><Label className="mb-1 block">Full name</Label><Input data-testid="guest-reg-name" value={f.name} onChange={set("name")} className="rounded-lg" /></div>
          )}
          <div><Label className="mb-1 block">Email</Label><Input data-testid="guest-auth-email" type="email" value={f.email} onChange={set("email")} className="rounded-lg" /></div>
          <div><Label className="mb-1 block">Password</Label><Input data-testid="guest-auth-password" type="password" value={f.password} onChange={set("password")} minLength={6} className="rounded-lg" /></div>
          {mode === "register" && (
            <div><Label className="mb-1 block">UAE mobile (WhatsApp)</Label><UaeMobileInput testid="guest-reg-mobile" value={f.mobile} onChange={(v) => { setF((p) => ({ ...p, mobile: v })); if (mobileErr) setMobileErr(""); }} />{mobileErr && <p className="mt-1 text-xs text-red-500" data-testid="guest-mobile-error">{mobileErr}</p>}</div>
          )}
          {mode === "register" && (
            <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-600" data-testid="guest-reg-terms-label">
              <input type="checkbox" data-testid="guest-reg-terms-accept" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-primary focus:ring-primary" />
              <span>I agree to SellStuff's <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-primary underline">Terms &amp; Conditions</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-primary underline">Privacy Policy</a>.</span>
            </label>
          )}
          <Button type="submit" data-testid="guest-auth-submit" disabled={loading || (mode === "register" && !termsAccepted)} className="w-full rounded-full bg-primary py-6 font-semibold text-white hover:bg-primary/90">
            {loading ? "Please wait…" : mode === "register" ? "Create account & publish" : "Log in & publish"}
          </Button>
        </form>
        <button
          type="button"
          data-testid="guest-auth-toggle"
          onClick={() => setMode(mode === "register" ? "login" : "register")}
          className="mx-auto mt-1 text-sm font-medium text-slate-500 underline hover:text-slate-700"
        >
          {mode === "register" ? "Already have an account? Log in" : "New to SellStuff? Create an account"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
